/* eslint-disable */
import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { setToken, setTokenBo } from "../utils/axios.create";
import { AUTH_CONSTANTS } from "../utils/auth";

/**
 * Hook to proactively refresh Auth0 tokens before they expire
 * This prevents failed API calls due to expired tokens
 *
 * @param enabled - Whether to enable proactive refresh (default: true)
 * @param intervalMs - Refresh interval in milliseconds (default: 5 minutes)
 */
export const useProactiveTokenRefresh = (
	enabled = true,
	intervalMs: number = AUTH_CONSTANTS.TOKEN_REFRESH_INTERVAL,
) => {
	const { getAccessTokenSilently, isAuthenticated } = useAuth0();
	const intervalRef = useRef<NodeJS.Timeout>();

	useEffect(() => {
		// Only run if authenticated and enabled
		if (!isAuthenticated || !enabled) {
			return;
		}

		const refreshToken = async () => {
			try {
				console.log("[TokenRefresh] Proactively refreshing token...");
				const token = await getAccessTokenSilently({
					cacheMode: "off", // Force fresh token
				});

				// Update axios instances
				setToken(token);
				setTokenBo(token);

				console.log("[TokenRefresh] Token refreshed successfully");
			} catch (error: any) {
				console.error("[TokenRefresh] Failed to refresh token:", error);

				// Don't throw error - let the 401 interceptor handle it on next API call
				// This prevents disrupting the user experience for transient errors
			}
		};

		// Initial refresh after a short delay
		const initialTimeout = setTimeout(refreshToken, 1000);

		// Set up periodic refresh
		intervalRef.current = setInterval(refreshToken, intervalMs);

		// Cleanup
		return () => {
			clearTimeout(initialTimeout);
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [isAuthenticated, enabled, intervalMs, getAccessTokenSilently]);
};
